import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from "@material-ui/core/Checkbox";

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    settingsContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
    opacity: {
        opacity: 0.5
    }
}));

export default function FormDetails(props) {

    const {
        form,
        status,
        groups,
        handleForm,
        handleStatus,
        isError,
        getErrorMessage,
        errors,
        newForm,
    } = props;

    const classes = useStyles();

    const getButtonNameMessage = useMemo(() => {
        if (isError('button_name')) return errors.button_name;
        return "Used for the New Form button and to describe individual forms.";
    }, [errors]);

    const getEntriesPerUserMessage = useMemo(() => {
        if (isError('entries_per_user')) return errors.entries_per_user;
        return "0 will allow no limit on entries per user."
    }, [errors]);

    const getNotificationGroupIdMessage = useMemo(() => {
        if (isError('notification_group_id')) return errors.notification_group_id;
        return "The group that will be notified when new entries are submitted or updated."
    }, [errors]);

    return (
        <Grid container direction="row" className={newForm ? classes.height : null}>
            {newForm &&
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-election.png' alt="election-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={newForm ? 7 : 12} className={newForm ? classes.formContainer : classes.settingsContainer}>
                <div className={classes.section}>
                    <Typography variant="h6" gutterBottom>
                        {newForm ? "New Form" : "Form Title"}
                    </Typography>
                    <TextField
                        id="input-name"
                        label="Name"
                        value={form.name}
                        margin="normal"
                        onChange={handleForm('name')}
                        fullWidth
                        autoFocus
                        error={isError('name')}
                        helperText={getErrorMessage('name')}
                        variant="outlined"
                        required
                    />
                    <TextField
                        id="input-description"
                        label="Description"
                        name="description"
                        value={form.description}
                        onChange={handleForm('description')}
                        fullWidth
                        error={isError('description')}
                        helperText={getErrorMessage('description')}
                        variant="outlined"
                        multiline
                        margin="normal"
                        rows="4"
                        rowsMax="4"
                        inputProps={{
                            maxLength: "255"
                        }}
                        required
                    />
                    <TextField
                        id="input-button-name"
                        label="Singular Name"
                        value={form.button_name}
                        margin="normal"
                        onChange={handleForm('button_name')}
                        fullWidth
                        placeholder='i.e. "New Member"'
                        error={isError('button_name')}
                        helperText={getButtonNameMessage}
                        variant="outlined"
                        required
                    />
                    <TextField
                        id="input-notification-group-id"
                        select
                        value={form.notification_group_id}
                        onChange={handleForm('notification_group_id')}
                        variant="outlined"
                        fullWidth
                        error={isError('notification_group_id')}
                        helperText={getNotificationGroupIdMessage}
                        >
                        <MenuItem value={0}>
                            No Notifications
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.section}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Form Status</FormLabel>
                        <RadioGroup aria-label="status" name="status" value={status} onChange={handleStatus}>
                            <FormControlLabel value="inactive" control={<Radio color="primary"/>} label="Inactive (Admin Only)" />
                            <FormControlLabel value="active" control={<Radio  color="primary"/>} label="Active" />
                            <FormControlLabel value="scheduled" control={<Radio  color="primary"/>} label="Scheduled" />
                        </RadioGroup>
                        <FormHelperText>*Administrators can always submit entries.</FormHelperText>
                    </FormControl>
                    <br/>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.global}
                                onChange={handleForm('global')}
                                value="global"
                                color="primary"
                            />
                        }
                        label="Should form be Globally accessible?"
                        disabled={(status === 'inactive')}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Select the group of users that can submit entries.
                    </Typography>
                    <TextField
                        id="input-group-id"
                        select
                        value={form.group_id}
                        onChange={handleForm('group_id')}
                        variant="outlined"
                        fullWidth
                        error={isError('group_id')}
                        helperText={getErrorMessage('group_id')}
                        disabled={(status === 'inactive')}
                        >
                        <MenuItem value={0}>
                            All Users
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many entries can a user make?
                    </Typography>
                    <TextField
                        id="input-entries-per-user"
                        value={form.entries_per_user}
                        onChange={handleForm('entries_per_user')}
                        fullWidth
                        type="number"
                        error={isError('entries_per_user')}
                        helperText={getEntriesPerUserMessage}
                        variant="outlined"
                        disabled={(status === 'inactive')}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time to begin accepting entries?
                    </Typography>
                    <DateTimePicker
                        inputVariant="outlined"
                        value={form.starts_at}
                        onChange={handleForm('starts_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={(status !== 'scheduled')}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time to end accepting entries?
                    </Typography>
                    <DateTimePicker
                        inputVariant="outlined"
                        value={form.ends_at}
                        onChange={handleForm('ends_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={(status !== 'scheduled')}
                    />
                </div>
            </Grid>
        </Grid>
    )
}
